import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { ordersService } from '../../services';
import { Order, OrderStatus } from '../../types/order.type';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

function relativeTime(iso: string | Date): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'ahora';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  return new Date(iso).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' });
}

const STATUS_DOT: Record<string, string> = {
  PAID: 'bg-green-500',
  PENDING: 'bg-yellow-400',
  CANCELLED: 'bg-gray-400',
};

const STATUS_LABEL_CLASS: Record<string, string> = {
  PAID: 'text-green-700 dark:text-green-400',
  PENDING: 'text-yellow-700 dark:text-yellow-400',
  CANCELLED: 'text-gray-500 dark:text-gray-400',
};

const Orders: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<OrderStatus | 'all'>('all');

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ordersService.getAll({});
      setOrders(Array.isArray(response.orders) ? response.orders : []);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders, user?.id]);

  const filtered = orders
    .filter((o) => {
      if (filterStatus !== 'all' && o.status !== filterStatus) return false;
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (o.id?.toLowerCase().includes(q)) || (o.productId?.toLowerCase().includes(q));
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const STATUS_PILLS: Array<{ value: OrderStatus | 'all'; label: string }> = [
    { value: 'all', label: t('orders.list.total') },
    { value: 'PAID', label: t('orders.list.paid') },
    { value: 'PENDING', label: t('orders.list.pending') },
    { value: 'CANCELLED', label: t('orders.list.cancelled') },
  ];

  const handleRowClick = (order: Order) => {
    if (order.status === 'PENDING') navigate(`/payment-selection/${order.id}`);
    else navigate(`/orders/${order.id}`);
  };

  const getStatusLabel = (status: OrderStatus): string => {
    const map: Record<string, string> = {
      PAID: t('orders.list.order.status.paid'),
      PENDING: t('orders.list.order.status.pending'),
      CANCELLED: t('orders.list.order.status.cancelled'),
    };
    return map[status] ?? status;
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-4">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('orders.list.title')}
          </h1>
          {!loading && (
            <span className="text-sm text-gray-400 dark:text-gray-500">{filtered.length}</span>
          )}
        </div>

        <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
          {/* Search */}
          <div className="relative">
            <SearchOutlined className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
            <input
              type="text"
              placeholder={t('orders.list.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-900 dark:focus:ring-gray-400 w-44"
            />
          </div>

          {/* Status pills */}
          <div className="flex items-center gap-1">
            {STATUS_PILLS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setFilterStatus(value)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  filterStatus === value
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-gray-900 dark:border-white'
                    : 'text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-400'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Refresh */}
          <button
            onClick={fetchOrders}
            disabled={loading}
            className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-40"
            title={t('common.refresh')}
          >
            <ReloadOutlined className={loading ? 'animate-spin' : ''} />
          </button>

          {/* Docs link */}
          <a
            href="https://docs.kubiy.com/billing"
            target="_blank"
            rel="noopener noreferrer"
            className="px-2.5 py-1 text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-md hover:border-gray-400 dark:hover:border-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            Documentación
          </a>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20">
          <span className="text-sm text-gray-400 dark:text-gray-500">{t('common.loading')}</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex justify-center py-20">
          <p className="text-sm text-gray-400 dark:text-gray-500">
            {searchTerm || filterStatus !== 'all'
              ? t('orders.list.noResults')
              : t('orders.list.emptyState.description')}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          {/* Table head — hidden on mobile */}
          <div className="hidden sm:grid grid-cols-[110px_1fr_130px_130px_70px] gap-4 px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">
            <span>{t('orders.list.order.order')}</span>
            <span>{t('orders.list.order.productId')}</span>
            <span className="text-right">Total</span>
            <span>Estado</span>
            <span className="text-right">Fecha</span>
          </div>

          {/* Rows */}
          {filtered.map((order) => (
            <div
              key={order.id}
              onClick={() => handleRowClick(order)}
              className="grid grid-cols-1 sm:grid-cols-[110px_1fr_130px_130px_70px] gap-1 sm:gap-4 items-start sm:items-center px-4 py-3.5 border-b border-gray-50 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer transition-colors"
            >
              <span className="font-mono text-sm text-gray-500 dark:text-gray-400">
                #{order.id.slice(0, 8)}
              </span>
              <span className="text-sm text-gray-800 dark:text-gray-200 truncate">
                {order.productId || '—'}
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-white tabular-nums sm:text-right">
                {order.total.toFixed(2)} {order.currency}
              </span>
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT[order.status] ?? 'bg-gray-400'}`} />
                <span className={`text-xs ${STATUS_LABEL_CLASS[order.status] ?? 'text-gray-500 dark:text-gray-400'}`}>
                  {getStatusLabel(order.status)}
                </span>
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500 sm:text-right tabular-nums">
                {relativeTime(order.createdAt)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Orders;
